import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { getAuthInfo } from '@/awsCodes/awsUtils';
import {
  ErrorResponse,
  BulkPostOrderRequest,
  BulkPostOrderResponseInfo,
  GetOrderResponseResult,
  OrderApi,
  PostOrderRequestInfo,
  SortType,
  UpdateOrderInfoRequest,
  UpdateOrderResponse,
  UpdateOrderResponseResult,
  UpdateOrderStatusApi,
  UpdateOrderStatusRequest,
  UseFilterItem,
  PostOrderResponseResult,
  UpdateOutputFlagRequest,
  UpdateOutputFlagApi,
  BulkDeleteOrderRequest,
  UpdateOrderMemoRequest,
  OrderMemoApi,
  UpsertOrderAndSingleSimulationApi,
  UpsertAndSingleSimulationOrderRequestInfo,
  UpsertAndSingleSimulationOrderResponseResult,
  UpsertAndSingleSimulationOrderResponse,
  OutputOrderResponseResult,
  OutputOrderApi,
  OutputOrderRequest,
  BulkPutOrderRequest,
  BulkPutOrderResponseResult,
  BulkPutOrderResponse,
  OrderBusinessApi,
  BulkRegisterOrderBusinessRequest,
  BulkRegisterOrderBusinessResponseResult,
  BulkUpdateOrderEtaInfo,
  BulkUpdateOrderEtaRequest,
} from '../axios/generated/orderManage/api';
import { IOrderRepository } from '../interfaces/IOrderRepository';
import { AxiosResponse } from 'axios';
import { IError } from '../interfaces/IError';
import { customAxiosInstance } from '../axios/axiosInstance';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { axiosErrorHandler } from '../axios/axiosError';
import { pollAsyncProcess } from '../composables/useAsyncPolling';

/**
 * 独自エラークラス
 */
class ExampleError extends Error {
  /**
   * コンストラクタ
   * @param number - エラー番号
   */
  constructor(number: string) {
    super(`Example API : 想定外のコード${number}が返却されました。`);
    this.name = 'ExampleError';
  }
}

/**
 * Axiosを使ったリポジトリ実装例
 */
export class OrderRepository implements IOrderRepository {
  /**
   * TODO 未完なのでこっちコピペしないでください！
   * とりあえず、bulkは考えず単体の1発注分の情報をputする
   * @param orderInfo - 登録する発注情報
   */
  async put(orderInfo: PostOrderRequestInfo): Promise<INetworkResult<PostOrderResponseResult, Error>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderApi = new OrderApi(undefined, undefined, axiosInstance);
    // トークンの取得
    let idToken: string | undefined;
    if (true) {
      const res: any = await getAuthInfo();
      if (!res) throw new Error('refreshToken expired');
      idToken = res.idToken;
    }

    // トークンが取得できなかったらエラーにする
    if (!idToken) throw Error;

    // APIを叩く
    const apiResponse = await orderApi.postOrder(orderInfo);

    switch (apiResponse.status) {
      case 200:
        const result: PostOrderResponseResult = apiResponse.data['result']!;
        return { status: { type: 'success', data: result } };
      default:
        return { status: { type: 'error', error: new ExampleError(apiResponse.status.toString()) } };
    }
  }

  /**
   * 発注情報のupsert&単発シミュレーション
   * バックエンドが非同期モード(statusCode=202)の場合はポーリングでシミュレーション完了を待機する。
   * 同期モード(statusCode=200)の場合は従来通り即座に結果を返す。
   * @param upsertAndSingleSimulateOrderInfo - 発注情報のupsert&単発シミュレーション
   * @returns 発注情報のupsert&単発シミュレーションの結果
   */
  async upsertAndSingleSimulate(
    upsertAndSingleSimulateOrderInfo: UpsertAndSingleSimulationOrderRequestInfo
  ): Promise<INetworkResult<UpsertAndSingleSimulationOrderResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderApi = new UpsertOrderAndSingleSimulationApi(undefined, undefined, axiosInstance);

    let apiResponse: AxiosResponse<UpsertAndSingleSimulationOrderResponse, ErrorResponse>;
    try {
      apiResponse = await orderApi.upsertAndSingleSimulationOrderSingleSimulation(upsertAndSingleSimulateOrderInfo);

      if (apiResponse.status !== 200) {
        throw new Error('想定外のレスポンス');
      }

      // body内statusCodeで同期/非同期を判定（API GWのHTTP statusは常に200）
      const bodyStatusCode = apiResponse.data.statusCode;

      if (bodyStatusCode === 202) {
        // 非同期モード: ポーリングでシミュレーション完了を待機
        const asyncResult = apiResponse.data.result as unknown as {
          requestId?: string;
          displayId?: string;
          orderId?: string;
          aggregateBusinessId?: string;
        };

        if (!asyncResult?.requestId) {
          throw new Error('非同期レスポンスにrequestIdが含まれていません');
        }

        const pollingResult = await pollAsyncProcess<{
          aggregateBusinessId?: string;
          recommendCreatedOrderDate?: number;
          recommendEstimateTime?: number;
          recommendArrivalValue?: number;
        }>(asyncResult.requestId, { actionType: 'single-simulation' });

        if (pollingResult.status === 'failed') {
          return {
            status: {
              type: 'error',
              error: {
                statusCode: 500,
                code: 'INTERNAL_SERVER_ERROR',
                message: pollingResult.errorMessage ?? 'シミュレーション処理が失敗しました',
              },
            },
          };
        }

        // DynamoDBのresultからシミュレーション結果を復元
        const simulationResult: UpsertAndSingleSimulationOrderResponseResult = {
          orderId: asyncResult.orderId ?? '',
          displayId: asyncResult.displayId,
          aggregateBusinessId: pollingResult.result?.aggregateBusinessId ?? asyncResult.aggregateBusinessId,
          recommendCreatedOrderDate: pollingResult.result?.recommendCreatedOrderDate,
          recommendEstimateTime: pollingResult.result?.recommendEstimateTime,
          recommendArrivalValue: pollingResult.result?.recommendArrivalValue,
        };
        return { status: { type: 'success', data: simulationResult } };
      }

      // 同期モード（statusCode=200）: 従来通り
      const result: UpsertAndSingleSimulationOrderResponseResult = apiResponse.data.result;
      return { status: { type: 'success', data: result } };
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注情報の取得
   * filterを考えず、ソート、limit offsetのみ考える
   * @param sortType - ソートタイプ
   * @param limit - リミット
   * @param offset - オフセット
   * @param isUnexportedOnly - 未出力のみ取得するか
   * @param sortTarget - ソート対象のカラム名
   * @param filterItem - フィルタリングに使用する項目
   * @param orderStatusFilterItem - 発注ステータスのフィルター
   * @param targetDate - カレンダーの日付
   * @param isLimitless - 全件取得するかどうか
   * @param aggregateFreeColumns - 集計取引自由カラムのフィルター情報
   */
  async get(
    sortType: SortType,
    limit: number,
    offset: number,
    isUnexportedOnly: boolean,
    sortTarget?: string,
    filterItem?: UseFilterItem,
    orderStatusFilterItem?: string,
    targetDate?: string,
    isLimitless?: boolean,
    aggregateFreeColumns?: string
  ): Promise<INetworkResult<GetOrderResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderApi = new OrderApi(undefined, undefined, axiosInstance);

    // APIを叩く

    try {
      const apiResponse = await orderApi.getOrder(
        sortType,
        limit,
        offset,
        isUnexportedOnly,
        sortTarget,
        filterItem,
        orderStatusFilterItem,
        targetDate,
        isLimitless,
        aggregateFreeColumns
      );
      switch (apiResponse.status) {
        case 200:
          const result: GetOrderResponseResult = apiResponse.data['result'];
          // return { status: { type: 'success', result: orderId } };
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注情報の一括登録
   * @param orderInfos - 発注情報の配列
   */
  async bulkPost(orderInfos: BulkPostOrderRequest): Promise<INetworkResult<BulkPostOrderResponseInfo, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderApi = new OrderApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await orderApi.bulkPostOrder(orderInfos);
      switch (apiResponse.status) {
        case 200:
          const result: BulkPostOrderResponseInfo = apiResponse.data['result'];
          // return { status: { type: 'success', result: orderId } };
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 編集モード時に編集するAPI
   * @param updateOrderInfoRequest - 編集を行う発注項目
   */
  async update(updateOrderInfoRequest: UpdateOrderInfoRequest): Promise<INetworkResult<UpdateOrderResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderApi = new OrderApi(undefined, undefined, axiosInstance);
    // APIを叩く
    let apiResponse: AxiosResponse<UpdateOrderResponse, ErrorResponse>;

    try {
      apiResponse = await orderApi.updateOrderInfo(updateOrderInfoRequest);
      switch (apiResponse.status) {
        case 200:
          const result: UpdateOrderResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注一括編集API
   * @param orderInfos - 編集する発注項目
   */
  async bulkPut(orderInfos: BulkPutOrderRequest): Promise<INetworkResult<BulkPutOrderResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderApi = new OrderApi(undefined, undefined, axiosInstance);

    let apiResponse: AxiosResponse<BulkPutOrderResponse, ErrorResponse>;

    try {
      apiResponse = await orderApi.bulkPutOrder(orderInfos);
      switch (apiResponse.status) {
        case 200:
          const result: BulkPutOrderResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注ステータスの一括更新
   * @param updateOrderStatusRequest - 更新するステータス情報
   */
  async updateOrderStatus(updateOrderStatusRequest: UpdateOrderStatusRequest): Promise<INetworkResult<UpdateOrderResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const updateOrderStatusApi = new UpdateOrderStatusApi(undefined, undefined, axiosInstance);
    // APIを叩く
    let apiResponse: AxiosResponse<UpdateOrderResponse, ErrorResponse>;

    try {
      apiResponse = await updateOrderStatusApi.updateOrderStatus(updateOrderStatusRequest);
      switch (apiResponse.status) {
        case 200:
          const result200: UpdateOrderResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result200 } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * TODO: そもそも発注機能自体をバックエンドに持っていくので、そこで内包されたらこれは消す
   * 発注/未発注フラグをtrueにする
   * @param updateOutputFlagRequest - 出力済みフラグを立てる発注情報
   */
  async updateOutputFlag(updateOutputFlagRequest: UpdateOutputFlagRequest): Promise<INetworkResult<UpdateOrderResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const updateOutputFlagApi = new UpdateOutputFlagApi(undefined, undefined, axiosInstance);
    // APIを叩く
    let apiResponse: AxiosResponse<UpdateOrderResponse, ErrorResponse>;

    try {
      apiResponse = await updateOutputFlagApi.updateOutputFlag(updateOutputFlagRequest);
      switch (apiResponse.status) {
        case 200:
          const result: UpdateOrderResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注情報の削除
   * @param orderInfos - 削除する発注情報
   */
  async bulkDelete(orderInfos: BulkDeleteOrderRequest): Promise<INetworkResult<UpdateOrderResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderApi = new OrderApi(undefined, undefined, axiosInstance);

    // APIを叩く
    let apiResponse: AxiosResponse<UpdateOrderResponse, ErrorResponse>;
    try {
      apiResponse = await orderApi.bulkDeleteOrder(orderInfos);
      switch (apiResponse.status) {
        case 200:
          const result: UpdateOrderResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注取引情報の登録
   * 取引情報ごとの発注情報を一括登録する
   * @param bulkRegisterOrderBusinessInfo - 発注取引情報の配列
   */
  async bulkRegisterOrderBusiness(
    bulkRegisterOrderBusinessInfo: BulkRegisterOrderBusinessRequest
  ): Promise<INetworkResult<BulkRegisterOrderBusinessResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderBusinessApi = new OrderBusinessApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await orderBusinessApi.bulkRegisterOrderBusiness(bulkRegisterOrderBusinessInfo);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注メモの更新
   * @param orderId - 更新する発注のID
   * @param updateOrderMemoRequest - 更新するメモ情報
   */
  async updateOrderMemo(orderId: string, updateOrderMemoRequest: UpdateOrderMemoRequest): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderMemoApi = new OrderMemoApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await orderMemoApi.updateOrderMemo(orderId, updateOrderMemoRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注メモの削除
   * @param orderId - 削除する発注のID
   */
  async deleteOrderMemo(orderId: string): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderMemoApi = new OrderMemoApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await orderMemoApi.clearOrderMemo(orderId);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注エクセルのテンプレートを取得
   * @param outputOrderRequest - 発注IDの配列
   */
  async outputOrder(outputOrderRequest: OutputOrderRequest): Promise<INetworkResult<OutputOrderResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const outputOrderApi = new OutputOrderApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await outputOrderApi.outputOrder(outputOrderRequest);
      const result = apiResponse.data.result;
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注納期を一括変更する
   * @param bulkUpdateOrderEtaRequest - リクエスト
   */
  async bulkUpdateEta(bulkUpdateOrderEtaRequest: BulkUpdateOrderEtaRequest): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderApi = new OrderApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await orderApi.bulkUpdateOrderEta(bulkUpdateOrderEtaRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}

const today = new Date();
const nextDay = new Date();
nextDay.setDate(nextDay.getDate() + 1);

/* APIの疎通確認 */
/**
 * 登録用のサンプルオブジェクト
 * このまま入れると2個目以上入らないので要改良
 */
const requestSample = {
  aggregateBusinessId: 'BBB',
  desiredDeliveryDate: DateService.formatDateToISOString(nextDay),
  estimatedTimeOfArrival: DateService.formatDateToISOString(nextDay),
  orderAmount: 3500.75,
  orderStatus: 'processing',
  outputManageFlag: false,
};

/**
 * API確認用の関数
 * あくまで確認する用で簡易化してます
 * @param orderRepository - リポジトリのインスタンス
 */
export const checkPutOrderRepositoryApi = async (orderRepository: OrderRepository) => {
  const res = await orderRepository.put(requestSample); // 設置されているサンプルを挿入
  console.log({ レスポンスチェック: res });
};
